// Attendance policy editor → "Preview on real data": the draft, unsaved, run
// over a department's (or one programme's) real registers from a recent
// period. Per student: attendance as shown today vs under the draft, who
// falls below the minimum, and the draft's custom columns; the header cells
// and a summary on top. Read-only — nothing is saved (attendance/previewPolicy).
import { useState } from "react";
import { AlertCircle, ArrowDown, ArrowUp, Loader2, Play, Search, Users } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DataPager, usePagedList } from "@/components/DataPager";
import { formatValue } from "@/lib/policyFormula";
import { useInstituteLabels } from "@/lib/instituteLabels";
import { type PolicySettings, type PreviewResult, PolicyError, previewPolicy } from "./policyApi";
import type { Programme } from "./structureApi";

const ALL = "__all__";
const PERIODS = [ 7, 30, 60, 90 ];

type Props = {
    settings: Partial<PolicySettings>;   // the draft as it would be saved
    departmentName: string;
    deptName: string;
    programmes: Programme[];
    reportHasProblems: boolean;
};

export function PolicyPreviewPanel({ settings, departmentName, deptName, programmes, reportHasProblems }: Props) {
    const L = useInstituteLabels();
    const [ scope, setScope ] = useState<string>(ALL);
    const [ days, setDays ] = useState(30);
    const [ result, setResult ] = useState<PreviewResult | null>(null);
    const [ running, setRunning ] = useState(false);
    const [ error, setError ] = useState<{ message: string; details: string[] } | null>(null);

    const list = usePagedList(result?.students || [], (s, q) => (s.name || "").toLowerCase().includes(q) || s.eid.toLowerCase().includes(q), 25);

    const run = async () => {
        setRunning(true);
        setError(null);
        try {
            setResult(await previewPolicy({ settings, departmentName, programmeId: scope === ALL ? null : scope, days }));
        } catch (err: any) {
            setError({ message: err?.message || "Preview failed.", details: err instanceof PolicyError ? err.details : [] });
        } finally {
            setRunning(false);
        }
    };

    const columns = settings.customColumns || [];
    const headers = settings.headerFields || [];
    const s = result?.summary;
    const stat = (label: string, value: string | number, tone?: "warn" | "ok") => (
        <div className="rounded-lg border bg-card p-3">
            <p className="text-[11px] text-muted-foreground">{label}</p>
            <p className={`text-xl font-semibold ${tone === "warn" ? "text-amber-700" : tone === "ok" ? "text-emerald-700" : ""}`}>{value}</p>
        </div>
    );

    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-muted/20 p-3">
                <div className="space-y-1">
                    <p className="text-[11px] text-muted-foreground">Run on</p>
                    <Select value={scope} onValueChange={setScope}>
                        <SelectTrigger className="h-9 w-72 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value={ALL}>All of {deptName}</SelectItem>
                            {programmes.map(p => <SelectItem key={p.id} value={p.id}>{L.programme}: {p.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-1">
                    <p className="text-[11px] text-muted-foreground">Registers from</p>
                    <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                        <SelectTrigger className="h-9 w-40 text-sm"><SelectValue>Last {days} days</SelectValue></SelectTrigger>
                        <SelectContent>{PERIODS.map(n => <SelectItem key={n} value={String(n)}>Last {n} days</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <Button onClick={run} disabled={running || reportHasProblems} className="h-9 gap-1.5">
                    {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} {result ? "Run again" : "Run preview"}
                </Button>
                <p className="text-xs text-muted-foreground">Uses the draft as it is now. Nothing is saved; registers never change.</p>
            </div>

            {reportHasProblems && <p className="text-sm text-amber-700">Fix the report columns first; the preview runs the formulas too.</p>}
            {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    <p className="flex items-center gap-2"><AlertCircle className="h-4 w-4" /> {error.message}</p>
                    {error.details.length > 0 && <ul className="ml-6 mt-1 list-disc">{error.details.map(d => <li key={d}>{d}</li>)}</ul>}
                </div>
            )}

            {!result && !running && !error && (
                <div className="flex flex-col items-center gap-2 py-16 text-center text-sm text-muted-foreground">
                    <Users className="h-10 w-10 text-muted-foreground/20" />
                    See what this draft would show before saving it: who changes, by how much, and who falls below the minimum.
                </div>
            )}

            {result && s && (
                <>
                    <p className="text-xs text-muted-foreground">{result.from} to {result.to} · {s.registers} register(s)</p>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                        {stat("Students", s.students)}
                        {stat("Shown differently", s.changed, s.changed ? "warn" : "ok")}
                        {stat("Largest change", s.largestChange ? `${s.largestChange > 0 ? "+" : ""}${s.largestChange} pts` : "—")}
                        {stat(s.minimumPercent === null ? "Below minimum (none set)" : `Below ${s.minimumPercent}%`, s.minimumPercent === null ? "—" : s.belowMinimum, s.belowMinimum ? "warn" : "ok")}
                        {stat("Students see it?", s.studentsSeeAttendance ? "Yes" : "No", s.studentsSeeAttendance ? undefined : "warn")}
                    </div>
                    {headers.length > 0 && (
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                            {headers.map(h => stat(h.name || h.key, formatValue(result.headers[ h.key ] ?? null, h.format, h.decimals)))}
                        </div>
                    )}

                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="relative w-full sm:w-72">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input className="h-9 pl-9" placeholder="Search students by name or ID…" value={list.query} onChange={(e) => list.setQuery(e.target.value)} />
                        </div>
                        <p className="text-xs text-muted-foreground">Largest change first{result.truncated ? " · showing the first 3,000" : ""}.</p>
                    </div>
                    <div className="overflow-x-auto rounded-lg border">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-muted/30">
                                    <TableHead className="pl-4">Student</TableHead>
                                    <TableHead className="text-right">Sessions</TableHead>
                                    <TableHead className="text-right">As today</TableHead>
                                    <TableHead className="text-right">Under this draft</TableHead>
                                    <TableHead className="text-right">Change</TableHead>
                                    {columns.map(c => <TableHead key={c.key} className="text-right text-primary">{c.name || c.key}</TableHead>)}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {list.rows.map(st => (
                                    <TableRow key={st.eid}>
                                        <TableCell className="pl-4">
                                            <div className="font-medium">{st.name || st.eid}</div>
                                            <div className="font-mono text-[11px] text-muted-foreground">{st.eid}</div>
                                        </TableCell>
                                        <TableCell className="text-right text-sm">{st.sessionsPresent}/{st.sessionsOn}</TableCell>
                                        <TableCell className="text-right text-sm">{st.todayPercent === null ? "—" : `${st.todayPercent}%`}</TableCell>
                                        <TableCell className="text-right text-sm">
                                            <span className="inline-flex items-center gap-1.5">
                                                {st.belowMinimum && <Badge variant="outline" className="border-amber-400 px-1 text-[10px] text-amber-700">below min</Badge>}
                                                {st.draftPercent === null ? "—" : `${st.draftPercent}%`}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right text-sm">
                                            {!st.change ? <span className="text-muted-foreground">—</span> : (
                                                <span className={`inline-flex items-center gap-0.5 ${st.change < 0 ? "text-red-600" : "text-emerald-700"}`}>
                                                    {st.change < 0 ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />}{Math.abs(st.change)}
                                                </span>
                                            )}
                                        </TableCell>
                                        {columns.map(c => (
                                            <TableCell key={c.key} className="text-right text-sm font-medium">
                                                {formatValue((st.columns?.[ c.key ] as number | null) ?? null, c.format, c.decimals)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <DataPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} />
                </>
            )}
        </div>
    );
}
